import L from "leaflet";

const CAP = 5000;
const CONCURRENCY = 10;

function esc(v) { return String(v ?? "").replaceAll("&","&amp;").replaceAll("<","&lt;").replaceAll(">","&gt;").replaceAll('"',"&quot;"); }
function unitsFactor(u) { return u === "ft" ? 3.28084 : u === "fm" ? 0.546807 : 1; }

let app;
let container = null;
let map = null;

// ── State ─────────────────────────────────────────────────────────────────────
let currentMarina = null;
let depthInfo = null;
let displayUnit = "ft";
let mode = "single";
let running = false;
let soundingPrecision = 1;
let soundingScale = 1;
let spacingInput = 5;
let soundings = [];
let seq = 1;
let slipOverlay = false;
let slipLayer = null;

let drawActive = false;
let vertices = [];
let drawHandlers = [];
let interactionKeyHandler = null;

let soundPane;
let soundLayer;
let drawLayer;

function setStatus(msg) { if (container) { const el = container.querySelector("#sde-status"); if (el) el.textContent = msg; } }
function formatDepth(depthBelow) { return Number(depthBelow * unitsFactor(displayUnit)).toFixed(soundingPrecision); }
function spacingToMeters(v) {
  if (displayUnit === "ft") return v * 0.3048;
  if (displayUnit === "fm") return v * 1.8288;
  return v;
}

// ── Slip overlay ──────────────────────────────────────────────────────────────
async function addSlipLayer(marina) {
  removeSlipLayer();
  if (!marina) return;
  try {
    const r = await fetch(`/api/slip-inventory?marina_id=${encodeURIComponent(marina)}&limit=5000`);
    if (!r.ok) return;
    const data = await r.json();
    const slips = data.items || [];
    slipLayer = L.featureGroup([], { pane: "lasSoundPane" }).addTo(map);
    slips.forEach((slip) => {
      if (!slip.geometry_wkt) return;
      try {
        const wkt = String(slip.geometry_wkt).trim();
        const ringMatch = wkt.match(/\(\s*([^()]+)\s*\)/);
        if (!ringMatch) return;
        const coords = ringMatch[1].trim().split(/\s*,\s*/).map((pair) => {
          const [x, y] = pair.trim().split(/\s+/);
          return [Number(y), Number(x)];
        }).filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
        if (coords.length < 3) return;
        L.polygon(coords, {
          pane: "lasSoundPane", color: "#22d3ee", weight: 1, opacity: 0.7,
          fillColor: "#22d3ee", fillOpacity: 0.1, interactive: false,
        }).addTo(slipLayer);
      } catch {}
    });
  } catch {}
}

function removeSlipLayer() {
  if (slipLayer) { try { slipLayer.clearLayers(); slipLayer.remove(); } catch {} slipLayer = null; }
}

// ── Sounding rendering ────────────────────────────────────────────────────────
function buildIcon(depthBelow) {
  const txt = formatDepth(depthBelow);
  const [intPart, fracPart = "0".repeat(soundingPrecision)] = txt.split(".");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" overflow="visible">
    <g style="transform:scale(${soundingScale});transform-origin:0 0;">
      <text y="0" dominant-baseline="middle" font-weight="800" font-size="12" fill="#00ffff" stroke="#000000" stroke-width="0.85" paint-order="stroke fill" style="font-family:system-ui,sans-serif;font-variant-numeric:tabular-nums;">
        <tspan x="0" text-anchor="end">${intPart}</tspan><tspan x="0" text-anchor="middle">.</tspan><tspan x="0" text-anchor="start">${fracPart}</tspan>
      </text>
    </g>
  </svg>`;
  return L.divIcon({ className: "sounding-label", html: svg, iconSize: [0, 0], iconAnchor: [0, 0] });
}

function redrawSoundings() {
  soundLayer.clearLayers();
  if (!soundings.length) return;
  const useLabels = map.getZoom() >= 15;
  soundings.forEach((s) => {
    if (useLabels) {
      L.marker([s.lat, s.lon], { icon: buildIcon(s.depth_m), interactive: false, pane: "sde-sound-pane" }).addTo(soundLayer);
    } else {
      L.circleMarker([s.lat, s.lon], {
        pane: "sde-sound-pane", radius: Math.max(1, Math.min(4, 1.2 * soundingScale)),
        stroke: false, fill: true, fillColor: "#00ffff", fillOpacity: 0.85, interactive: false,
      }).addTo(soundLayer);
    }
  });
}

// ── Depth query ───────────────────────────────────────────────────────────────
async function queryDepth(lng, lat) {
  if (!currentMarina) return null;
  const r = await fetch(`/api/depth/query?marina=${encodeURIComponent(currentMarina)}&lng=${lng}&lat=${lat}`);
  if (!r.ok) return null;
  const data = await r.json();
  return Number.isFinite(data.depth_m) ? data.depth_m : null;
}

// ── Geometry helpers ──────────────────────────────────────────────────────────
function haversine(a, b) {
  const R = 6371008.8;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLon = (b.lng - a.lng) * Math.PI / 180;
  const sin2 = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(sin2));
}

function sampleAlongLine(latlngs, spacingM) {
  if (latlngs.length < 2) return [];
  const out = [{ lat: latlngs[0].lat, lng: latlngs[0].lng }];
  let carry = 0;
  for (let i = 1; i < latlngs.length; i++) {
    const segStart = latlngs[i - 1], segEnd = latlngs[i];
    const segLen = haversine(segStart, segEnd);
    let walked = carry;
    while (walked + spacingM <= segLen) {
      const t = (walked + spacingM) / segLen;
      out.push({ lat: segStart.lat + t * (segEnd.lat - segStart.lat), lng: segStart.lng + t * (segEnd.lng - segStart.lng) });
      walked += spacingM;
    }
    carry = walked + spacingM - segLen > 0 ? walked + spacingM - segLen : 0;
    if (Math.abs(carry - spacingM) < 0.01) carry = 0;
  }
  return out;
}

function pointInPoly(p, vs) {
  let inside = false;
  for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
    const xi = vs[i].lng, yi = vs[i].lat, xj = vs[j].lng, yj = vs[j].lat;
    if (((yi > p.lat) !== (yj > p.lat)) && (p.lng < ((xj - xi) * (p.lat - yi)) / (yj - yi) + xi)) inside = !inside;
  }
  return inside;
}

function gridInsidePolygon(latlngs, spacing) {
  const lats = latlngs.map((p) => p.lat), lngs = latlngs.map((p) => p.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const dlat = spacing / 111320;
  const dlon = spacing / (111320 * Math.cos(((minLat + maxLat) * Math.PI) / 360));
  const pts = [];
  for (let lat = minLat; lat <= maxLat; lat += dlat)
    for (let lng = minLng; lng <= maxLng; lng += dlon)
      if (pointInPoly({ lat, lng }, latlngs)) pts.push({ lat, lng });
  return pts;
}

// ── Interaction ───────────────────────────────────────────────────────────────
function bindMap(type, handler) { map.on(type, handler); drawHandlers.push(() => map.off(type, handler)); }
function clearMapHandlers() { drawHandlers.splice(0).forEach((off) => { try { off(); } catch {} }); }
function resetDrawing() { drawActive = false; vertices = []; drawLayer.clearLayers(); clearMapHandlers(); }

function stopInteraction() {
  running = false; resetDrawing(); map.getContainer().style.cursor = "";
  if (interactionKeyHandler) { document.removeEventListener("keydown", interactionKeyHandler); interactionKeyHandler = null; }
  setTimeout(() => map.doubleClickZoom?.enable?.(), 0);
}

async function runSingleClick(e) {
  if (!currentMarina || !depthInfo?.rasterReady) { setStatus("no depth layer ready"); return; }
  setStatus("querying…");
  try {
    const depth_m = await queryDepth(e.latlng.lng, e.latlng.lat);
    if (depth_m === null) { setStatus("no data at point"); return; }
    const depthBelow = Math.max(0, -depth_m);
    soundings.push({ id: `p${seq++}`, lat: e.latlng.lat, lon: e.latlng.lng, depth_m: depthBelow });
    redrawSoundings();
    setStatus(`${soundings.length} soundings — last: ${formatDepth(depthBelow)} ${displayUnit}`);
    render();
  } catch (err) { setStatus(`error: ${err.message}`); }
}

async function runBatch(points) {
  const capped = points.slice(0, CAP);
  if (points.length > CAP) setStatus(`capped at ${CAP.toLocaleString()}`);
  let kept = 0, done = 0;
  for (let i = 0; i < capped.length; i += CONCURRENCY) {
    if (!running) break;
    const chunk = capped.slice(i, i + CONCURRENCY);
    const results = await Promise.all(chunk.map(async (pt) => {
      try { return { pt, depth_m: await queryDepth(pt.lng ?? pt.lon, pt.lat) }; }
      catch { return { pt, depth_m: null }; }
    }));
    for (const { pt, depth_m } of results) {
      done++;
      if (depth_m === null) continue;
      soundings.push({ id: `p${seq++}`, lat: pt.lat, lon: pt.lng ?? pt.lon, depth_m: Math.max(0, -depth_m) });
      kept++;
    }
    setStatus(`${Math.round(done / capped.length * 100)}% — ${done}/${capped.length} sampled, ${kept} kept`);
    redrawSoundings(); render();
    await new Promise(r => requestAnimationFrame(r));
  }
  return { done, kept };
}

function beginInteraction() {
  if (!currentMarina || !depthInfo?.rasterReady) { setStatus("no depth layer ready — build raster first"); return; }
  stopInteraction(); running = true; render();
  interactionKeyHandler = (e) => { if (e.key !== "Escape") return; stopInteraction(); setStatus("cancelled"); render(); };
  document.addEventListener("keydown", interactionKeyHandler);
  if (mode === "single") {
    map.getContainer().style.cursor = "crosshair"; map.doubleClickZoom?.disable?.();
    bindMap("click", runSingleClick);
    bindMap("dblclick", (e) => { L.DomEvent.stop(e); stopInteraction(); setStatus("idle"); render(); });
    return;
  }
  drawActive = true; vertices = []; map.getContainer().style.cursor = "crosshair"; map.doubleClickZoom?.disable?.();
  const tempLine = L.polyline([], { color: "#22d3ee", weight: 2, opacity: 0.8, dashArray: "4 4" }).addTo(drawLayer);
  const tempPoly = L.polygon([], { color: "#22d3ee", weight: 2, opacity: 0.6, fillOpacity: 0.1 }).addTo(drawLayer);
  const updateTemp = () => { if (mode === "grid") { tempPoly.setLatLngs([vertices]); tempLine.setLatLngs(vertices); } else { tempLine.setLatLngs(vertices); tempPoly.setLatLngs([]); } };
  bindMap("click", (e) => { if (!drawActive) return; vertices.push(e.latlng); updateTemp(); });
  bindMap("dblclick", async (e) => {
    L.DomEvent.stop(e); if (!drawActive) return;
    const verts = vertices.slice();
    if (e?.latlng) { const last = verts[verts.length - 1]; if (!last || Math.abs(last.lat - e.latlng.lat) > 1e-10 || Math.abs(last.lng - e.latlng.lng) > 1e-10) verts.push(e.latlng); }
    const uniqueCount = new Set(verts.map(v => `${v.lat.toFixed(7)},${v.lng.toFixed(7)}`)).size;
    drawActive = false; resetDrawing(); map.getContainer().style.cursor = "";
    setTimeout(() => map.doubleClickZoom?.enable?.(), 0);
    const spacingM = Math.max(0.1, spacingToMeters(Math.max(0.01, Number(spacingInput || 5))));
    let pts = [];
    if (mode === "grid" && uniqueCount >= 3) pts = gridInsidePolygon(verts, spacingM).map(p => ({ lat: p.lat, lng: p.lng }));
    else if (mode === "section" && uniqueCount >= 2) pts = sampleAlongLine(verts, spacingM);
    else { setStatus("not enough vertices"); running = false; render(); return; }
    setStatus(`sampling ${pts.length} points…`);
    const { done, kept } = await runBatch(pts);
    setStatus(`done — ${done} sampled, ${kept} kept`);
    running = false; render();
  });
}

function clearSoundings() { soundings = []; soundLayer.clearLayers(); render(); setStatus("cleared"); }

function exportGeoJSON() {
  if (!soundings.length) return;
  const fc = { type: "FeatureCollection", features: soundings.map(s => ({ type: "Feature", properties: { id: s.id, depth_m: s.depth_m }, geometry: { type: "Point", coordinates: [s.lon, s.lat] } })) };
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([JSON.stringify(fc, null, 2)], { type: "application/geo+json" }));
  a.download = `soundings_${Date.now()}.geojson`;
  document.body.appendChild(a); a.click(); setTimeout(() => a.remove(), 250);
  setStatus("exported GeoJSON");
}

function exportXYZ() {
  if (!soundings.length) return;
  const factor = unitsFactor(displayUnit);
  const rows = soundings.filter(s => Number.isFinite(s.depth_m)).map(s => `${s.lon} ${s.lat} ${(s.depth_m * factor).toFixed(soundingPrecision)}`);
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([["# HydroTools soundings", `# units=${displayUnit}`, ...rows].join("\n")], { type: "text/plain;charset=utf-8" }));
  a.download = `soundings_${Date.now()}.xyz`;
  document.body.appendChild(a); a.click(); setTimeout(() => a.remove(), 250);
  setStatus("exported XYZ");
}

// ── Render ────────────────────────────────────────────────────────────────────
function render() {
  if (!container) return;
  const hasDepth = !!depthInfo?.rasterReady;
  const btnSty = `background:rgba(148,163,184,0.08);border:1px solid rgba(148,163,184,0.18);color:var(--text);border-radius:4px;padding:2px 9px;font-size:11px;cursor:pointer;`;
  const modeBtnSty = (active) => `${btnSty}${active ? "border-color:#38bdf8;color:#38bdf8;" : ""}`;

  const marinaLabel = currentMarina
    ? `<span style="color:var(--text);font-weight:500;">${esc(currentMarina)}</span>`
    : `<span style="color:var(--muted);">No marina selected</span>`;

  const depthStatus = !currentMarina
    ? `<div class="small" style="color:var(--muted);margin-top:4px;">Select an active marina in Image Generator → Project Settings.</div>`
    : !hasDepth
      ? `<div class="small" style="color:#f59e0b;margin-top:4px;">${depthInfo ? "Raster building…" : "No depth raster configured."}</div>`
      : `<div class="small" style="color:#22c55e;margin-top:4px;">Depth raster ready.</div>`;

  container.innerHTML = `
    <div style="border:1px solid rgba(148,163,184,0.18);border-radius:8px;overflow:hidden;">
      <div style="display:flex;align-items:center;padding:7px 10px;background:rgba(2,6,23,0.5);border-bottom:1px solid rgba(148,163,184,0.12);">
        <span class="small" style="font-weight:600;flex:1;">SLIP DEPTH EXTRACTION</span>
      </div>

      <div style="padding:10px;">
        <div class="small" style="color:var(--muted);">Marina: ${marinaLabel}</div>
        ${depthStatus}
      </div>

      ${hasDepth ? `
      <div style="border-top:1px solid rgba(148,163,184,0.1);padding:10px;">
        <div class="small" style="font-weight:600;margin-bottom:8px;">Soundings</div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:8px;">
          <button id="sde-mode-single" style="${modeBtnSty(mode==="single")}">Single</button>
          <button id="sde-mode-section" style="${modeBtnSty(mode==="section")}">Transect</button>
          <button id="sde-mode-grid" style="${modeBtnSty(mode==="grid")}">Grid</button>
        </div>
        ${mode !== "single" ? `
          <div style="margin-bottom:8px;">
            <label class="small">Spacing (${esc(displayUnit)})
              <input id="sde-spacing" type="number" min="0.1" step="0.5" value="${spacingInput}" style="margin-left:6px;width:70px;" />
            </label>
          </div>
        ` : ""}
        <div style="margin-bottom:8px;">
          <label class="small">Precision
            <select id="sde-prec" style="margin-left:6px;">
              ${[0,1,2].map(v=>`<option value="${v}" ${soundingPrecision===v?"selected":""}>${v} dec</option>`).join("")}
            </select>
          </label>
        </div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:8px;">
          ${running
            ? `<button id="sde-stop" style="${btnSty}">Stop</button>`
            : `<button id="sde-start" style="${btnSty}">Start</button>`
          }
          <button id="sde-clear" style="${btnSty}">Clear (${soundings.length})</button>
        </div>
        ${soundings.length ? `
          <div style="display:flex;gap:6px;margin-bottom:8px;">
            <button id="sde-export-geojson" style="${btnSty}">Export GeoJSON</button>
            <button id="sde-export-xyz" style="${btnSty}">Export XYZ</button>
          </div>
        ` : ""}
        <div id="sde-status" class="small" style="color:var(--muted);min-height:16px;"></div>
      </div>

      <div style="border-top:1px solid rgba(148,163,184,0.1);padding:10px;">
        <div class="small" style="font-weight:600;margin-bottom:8px;">Slip Overlay</div>
        <label class="small"><input id="sde-slip-overlay" type="checkbox" ${slipOverlay?"checked":""} style="margin-right:5px;" />Show slips on map</label>
      </div>

      <div style="border-top:1px solid rgba(148,163,184,0.1);padding:10px;">
        <div class="small" style="font-weight:600;margin-bottom:6px;">Populate Slip Depths</div>
        <div class="small" style="color:var(--muted);margin-bottom:8px;">Reads depth raster at each slip centroid and writes back to the slip inventory.</div>
        <button id="sde-populate-slips" style="${btnSty}">Run for ${esc(currentMarina)}</button>
        <div id="sde-slip-result" class="small" style="color:var(--muted);margin-top:6px;min-height:16px;"></div>
      </div>
      ` : ""}
    </div>
  `;
  bindEvents();
}

function bindEvents() {
  if (!container) return;
  container.querySelector("#sde-mode-single")?.addEventListener("click", () => { mode = "single"; render(); });
  container.querySelector("#sde-mode-section")?.addEventListener("click", () => { mode = "section"; render(); });
  container.querySelector("#sde-mode-grid")?.addEventListener("click", () => { mode = "grid"; render(); });
  container.querySelector("#sde-spacing")?.addEventListener("change", (e) => { spacingInput = Math.max(0.01, Number(e.target.value) || 5); });
  container.querySelector("#sde-prec")?.addEventListener("change", (e) => { soundingPrecision = Number(e.target.value); redrawSoundings(); });
  container.querySelector("#sde-start")?.addEventListener("click", () => beginInteraction());
  container.querySelector("#sde-stop")?.addEventListener("click", () => { stopInteraction(); setStatus("stopped"); render(); });
  container.querySelector("#sde-clear")?.addEventListener("click", clearSoundings);
  container.querySelector("#sde-export-geojson")?.addEventListener("click", exportGeoJSON);
  container.querySelector("#sde-export-xyz")?.addEventListener("click", exportXYZ);
  container.querySelector("#sde-slip-overlay")?.addEventListener("change", (e) => {
    slipOverlay = e.target.checked;
    if (slipOverlay) addSlipLayer(currentMarina);
    else removeSlipLayer();
  });
  container.querySelector("#sde-populate-slips")?.addEventListener("click", async () => {
    const btn = container.querySelector("#sde-populate-slips");
    const resultEl = container.querySelector("#sde-slip-result");
    if (btn) btn.disabled = true;
    if (resultEl) resultEl.textContent = "running…";
    try {
      const r = await fetch("/api/depth/populate-slip-depths", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ marina: currentMarina }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      if (resultEl) resultEl.textContent = `${data.updated} updated, ${data.nodata} no data, ${data.errors} errors (${data.total} slips)`;
    } catch (err) {
      if (resultEl) resultEl.textContent = `error: ${err.message}`;
    } finally {
      if (btn) btn.disabled = false;
    }
  });
}

// ── Public runtime (called by image-generator on marina switch) ───────────────

export function createSlipDepthExtractionRuntime(appContext) {
  app = appContext;
  map = app.map.instance;

  try { soundPane = map.createPane("sde-sound-pane"); } catch { soundPane = map.getPane("sde-sound-pane"); }
  soundPane.style.zIndex = "820";
  soundLayer = L.featureGroup([], { pane: "sde-sound-pane" }).addTo(map);
  drawLayer = L.featureGroup([], { pane: "sde-sound-pane" }).addTo(map);

  map.on("zoomend", () => { if (soundings.length) redrawSoundings(); });

  return {
    mount(mountEl) { container = mountEl; render(); },
    destroy() {
      stopInteraction(); removeSlipLayer();
      try { soundLayer.clearLayers(); soundLayer.remove(); } catch {}
      try { drawLayer.clearLayers(); drawLayer.remove(); } catch {}
      container = null;
    },
    setDepthLayer(marina, info) {
      currentMarina = marina || null;
      depthInfo = info || null;
      render();
    },
    clearForMarinaSwitch() {
      slipOverlay = false;
      removeSlipLayer();
      render();
    },
  };
}

// ── Standalone tool definition ────────────────────────────────────────────────

let _runtime = null;

export const slipDepthExtractionTool = {
  id: "slip-depth-extraction",
  name: "Slip Depth Extraction",
  init(appContext) {
    app = appContext;
    _runtime = createSlipDepthExtractionRuntime(appContext);
  },
  destroy() {
    _runtime?.destroy?.();
    _runtime = null;
  },
  renderPanel(_appContext, panelEl) {
    if (_runtime) _runtime.mount(panelEl);
  },
};
